import { useCallback, useEffect, useRef, useState } from 'react'
import {
  checkAndroidUpdateAvailability,
  startAndroidUpdate,
  watchFlexibleUpdateDownloaded,
  type AndroidUpdatePrompt,
} from '../services/android-app-update'
import { isUpdateCheckDue } from '../utils/android-update-prompt-policy'
import { getNativePlatform } from '../utils/platform'

/** Pre-fix keys; `dismissed-code` hid a skipped versionCode forever. */
const LEGACY_STORAGE_KEYS = ['ascend-android-update-dismissed-code', 'ascend-android-update-last-check']

function clearLegacyStorage(): void {
  try {
    for (const key of LEGACY_STORAGE_KEYS) localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/**
 * Soft Android update prompt: checks Play on cold start and return-to-foreground.
 * If Play In-App Updates reports nothing (typical for internal testing), falls
 * back to a published versionCode so testers still see our modal.
 *
 * "Not now" only snoozes (see `android-update-prompt-policy`). While
 * `suppressed` (call, recording, sign-in, splash) an owed prompt is held off
 * screen and appears as soon as it clears.
 */
export function useAndroidAppUpdate({ suppressed }: { suppressed: boolean }) {
  const [owed, setOwed] = useState<AndroidUpdatePrompt | null>(null)
  const [busy, setBusy] = useState(false)
  const checkingRef = useRef(false)
  const owedRef = useRef<AndroidUpdatePrompt | null>(null)
  owedRef.current = owed
  const lastCheckAtRef = useRef<number | null>(null)
  const lastShownAtRef = useRef<number | null>(null)

  const prompt = suppressed ? null : owed

  useEffect(() => {
    if (!prompt) return
    lastShownAtRef.current = Date.now()
    return () => {
      lastShownAtRef.current = Date.now()
    }
  }, [prompt])

  const runCheck = useCallback(async () => {
    if (checkingRef.current || owedRef.current) return
    const now = Date.now()
    if (!isUpdateCheckDue({ lastCheckAt: lastCheckAtRef.current, lastShownAt: lastShownAtRef.current, now })) {
      return
    }

    checkingRef.current = true
    lastCheckAtRef.current = now
    try {
      const next = await checkAndroidUpdateAvailability()
      setOwed((prev) => prev ?? next)
    } finally {
      checkingRef.current = false
    }
  }, [])

  useEffect(() => {
    if (getNativePlatform() !== 'android') return

    clearLegacyStorage()
    void runCheck()

    let removeAppListener: (() => void) | null = null
    let removeFlexibleListener: (() => void) | null = null
    let cancelled = false

    void import('@capacitor/app').then(({ App }) =>
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void runCheck()
      }).then((handle) => {
        if (cancelled) void handle.remove()
        else removeAppListener = () => void handle.remove()
      }),
    )

    void watchFlexibleUpdateDownloaded(() => {
      void checkAndroidUpdateAvailability().then((next) => {
        if (next?.readyToInstall) setOwed(next)
      })
    }).then((unsub) => {
      if (cancelled) unsub()
      else removeFlexibleListener = unsub
    })

    return () => {
      cancelled = true
      removeAppListener?.()
      removeFlexibleListener?.()
    }
  }, [runCheck])

  const dismiss = useCallback(() => {
    setOwed(null)
  }, [])

  const updateNow = useCallback(async () => {
    const current = owedRef.current
    if (!current || busy) return
    setBusy(true)
    try {
      const result = await startAndroidUpdate(current)
      if (result === 'cancelled' || result === 'failed') {
        // Keep the modal open so they can retry or dismiss.
        return
      }
      // started: flexible download began — hidden until the DOWNLOADED listener
      // resurfaces it. completed / opened-store: done here.
      setOwed(null)
    } finally {
      setBusy(false)
    }
  }, [busy])

  return {
    prompt,
    busy,
    dismiss,
    updateNow,
  }
}
